import { type MutableRefObject, useCallback, useRef } from "react";

import { useDebouncedSave, useReplaceSoilSightCropYears, useUpdateSoilSightProjectSettingsRefinements, useUpdateSoilSightScenario } from "utils";

import type { ReplaceCropYearsBody, UpdateSettingsRefinementsBody, UpdateSoilSightScenarioBody } from "models";

import { isApiScenarioId, showSoilSightSaveFailed } from "pages/app/scenario-analysis/utils/scenarioAnalysisHelpers";

type SettingsAutosaveArgs = {
  isDirty: boolean;
  payload: UpdateSettingsRefinementsBody;
  delay: number;
  ensureProjectId: () => Promise<string | null>;
  settingsSavedRef: MutableRefObject<string | null>;
  /** Read on unmount: true saves an edit still in its debounce window instead of dropping it. */
  flushOnUnmount?: () => boolean;
  t: (key: string) => string;
};

export const useSoilSightDebouncedSettingsAutosave = ({
  isDirty,
  payload,
  delay,
  ensureProjectId,
  settingsSavedRef,
  flushOnUnmount,
  t,
}: SettingsAutosaveArgs): void => {
  const { mutateAsync: updateSettingsAsync } = useUpdateSoilSightProjectSettingsRefinements();

  useDebouncedSave({
    value: payload,
    delay,
    serialize: (body) => JSON.stringify(body),
    savedRef: settingsSavedRef,
    shouldSkip: () => !isDirty,
    flushOnUnmount,
    save: async (body, request) => {
      try {
        const id = await ensureProjectId();
        if (!request.isLatestSave() || !id) return false;
        await updateSettingsAsync({ soilSightProjectId: id, body });
        return request.isLatestSave();
      } catch (saveError) {
        if (request.isLatestSave()) showSoilSightSaveFailed(t, saveError);
        return false;
      }
    },
  });
};

/** One scenario's pending payload: its name, or its crop years. */
type ScenarioPayloadRow<Body> = { id: string; body: Body };

/** One started save, and whether it is still the newest one for its scenario. */
type ScenarioRowSave = { promise: Promise<void>; isLatest: () => boolean };

/** True when a saved scenario's payload differs from what its ledger last recorded. */
export const isRowUnsaved = <Body>(savedRef: MutableRefObject<Map<string, string>>, { id, body }: ScenarioPayloadRow<Body>): boolean =>
  isApiScenarioId(id) && savedRef.current.get(id) !== JSON.stringify(body);

/** Saves the rows that differ from the ledger, a later save superseding an earlier one; `shouldRecord` false leaves the ledger untouched. */
type SaveChangedRows<Body> = (rows: ScenarioPayloadRow<Body>[], shouldRecord?: () => boolean) => ScenarioRowSave[];

type ScenarioAutosaveArgs<Body> = {
  isDirty: boolean;
  soilSightProjectId: string | null;
  hasHydratedRef: MutableRefObject<boolean>;
  rows: ScenarioPayloadRow<Body>[];
  delay: number;
  /** Autosave ledger: last saved payload per scenario id. */
  savedRef: MutableRefObject<Map<string, string>>;
  /** Read on unmount: true saves an edit still in its debounce window instead of dropping it. */
  flushOnUnmount?: () => boolean;
  t: (key: string) => string;
  /** Runs when the newest save of a scenario lands. It is a dependency of the save, so we keep it stable. */
  onRowSaved?: (scenarioId: string) => void;
};

const usePerScenarioAutosave = <Body>({
  isDirty,
  soilSightProjectId,
  hasHydratedRef,
  rows,
  delay,
  savedRef,
  flushOnUnmount,
  t,
  onRowSaved,
  mutate,
}: ScenarioAutosaveArgs<Body> & { mutate: (args: { scenarioId: string; body: Body; requestVersion: number }) => Promise<unknown> }): SaveChangedRows<Body> => {
  const latestRequestVersionRef = useRef<Map<string, number>>(new Map());
  const rowsRef = useRef(rows);
  rowsRef.current = rows;

  const saveChangedRows = useCallback<SaveChangedRows<Body>>(
    function saveChangedRows(pendingRows, shouldRecord = () => true) {
      return pendingRows.flatMap(({ id, body }) => {
        if (!isRowUnsaved(savedRef, { id, body })) return [];
        const serialized = JSON.stringify(body);
        const requestVersion = (latestRequestVersionRef.current.get(id) ?? 0) + 1;
        latestRequestVersionRef.current.set(id, requestVersion);
        const isLatest = () => latestRequestVersionRef.current.get(id) === requestVersion;
        const promise = mutate({ scenarioId: id, body, requestVersion }).then(() => {
          if (!isLatest() || !shouldRecord()) return;
          const savedBefore = savedRef.current.get(id);
          savedRef.current.set(id, serialized);
          onRowSaved?.(id);
          // Changed back to the old saved value while this save was out, so the autosave skipped it; sent now so the server matches the page.
          const currentRow = rowsRef.current.find((row) => row.id === id);
          if (!currentRow || JSON.stringify(currentRow.body) !== savedBefore || !isRowUnsaved(savedRef, currentRow)) return;
          saveChangedRows([currentRow], shouldRecord).forEach((resave) => {
            resave.promise.catch((saveError) => {
              if (resave.isLatest()) showSoilSightSaveFailed(t, saveError);
            });
          });
        });
        return [{ promise, isLatest }];
      });
    },
    [mutate, savedRef, t, onRowSaved]
  );

  useDebouncedSave({
    value: rows,
    delay,
    shouldSkip: () => !isDirty || (!hasHydratedRef.current && !soilSightProjectId),
    flushOnUnmount,
    save: async (pendingRows) => {
      saveChangedRows(pendingRows).forEach(({ promise, isLatest }) => {
        promise.catch((saveError) => {
          if (isLatest()) showSoilSightSaveFailed(t, saveError);
        });
      });
    },
  });

  return saveChangedRows;
};

export const useSoilSightDebouncedScenarioMetaAutosave = (
  args: ScenarioAutosaveArgs<UpdateSoilSightScenarioBody>
): SaveChangedRows<UpdateSoilSightScenarioBody> => {
  const { mutateAsync: updateScenarioAsync } = useUpdateSoilSightScenario();
  return usePerScenarioAutosave({ ...args, mutate: updateScenarioAsync });
};

export const useSoilSightDebouncedCropYearsAutosave = (args: ScenarioAutosaveArgs<ReplaceCropYearsBody>): SaveChangedRows<ReplaceCropYearsBody> => {
  const { mutateAsync: replaceCropYearsAsync } = useReplaceSoilSightCropYears();
  return usePerScenarioAutosave({ ...args, mutate: replaceCropYearsAsync });
};
